import { Component, Inject, LOCALE_ID } from '@angular/core';
import { localizeRole, Role } from '../../../model/auth/Role';
import { MobileAwareComponent } from '../../../common/mobile-aware/mobile-aware.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TenantService } from '../../../service/tenant.service';
import { Contest } from '../../../model/contest/Contest';
import { ContestPartStats, localizeUserExamGrade, UserExamGrade } from '../../../model/stats/Stats';
import { NotificationHandler } from '../../../helper/notification.handler';
import { StatsService } from '../../../service/stats.service';
import { StatsHelper } from '../../../helper/stats.helper';
import { localizePaymentStatus, PaymentStatus } from '../../../model/Payment';

@Component({
  selector: 'app-contest-participation-stats',
  templateUrl: './contest-participation-stats.component.html',
  styleUrls: ['./contest-participation-stats.component.scss']
})
export class ContestParticipationStatsComponent extends MobileAwareComponent {

  readonly roles = [Role.Host, Role.Coordinator];

  competitionId?: string;
  contest?: Contest;

  stats?: ContestPartStats;

  paymentStats?: number[];
  paymentStatsLabels = [
    ...StatsHelper.paymentStatusChartLabels(),
    localizePaymentStatus(PaymentStatus.canceled)
  ];

  gradeStats?: number[];
  gradeStatsLabels = StatsHelper.gradeChartLabels();

  participantStatusStats?: number[];
  participantStatusStatsLabels = StatsHelper.userStatusChartLabels();

  otherUserStatusStats?: number[];
  otherUserStatusStatsLabels = [
    `${localizeRole(Role.Teacher)}: ${localizePaymentStatus(PaymentStatus.successful)}`,
    `${localizeRole(Role.Teacher)}: ${localizePaymentStatus(PaymentStatus.review)}`,
    `${localizeRole(Role.Teacher)}: ${localizePaymentStatus(PaymentStatus.rejected)}`,
    `${localizeRole(Role.Coordinator)}: ${localizePaymentStatus(PaymentStatus.successful)}`,
  ];

  examParticipantStatusStats?: number[];
  examParticipantStatsLabels = [
    $localize`:General|Registered title@@general-registered-title:Registered`,
    $localize`:General|Participated title@@general-participated-title:Participated`,
  ];

  examGradeStats?: number[];
  examGradeStatsLabels = StatsHelper.gradeChartLabels();

  examPointsGradeStats?: number[];
  examPointsGradeStatsLabels = [
    localizeUserExamGrade(UserExamGrade.excellent),
    localizeUserExamGrade(UserExamGrade.good),
    localizeUserExamGrade(UserExamGrade.average),
  ];

  constructor(private statsService: StatsService,
              private tenantService: TenantService,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }
  }

  loadContestStats(contest?: Contest) {
    if (!contest) return;

    this.statsService.getContestPartStats(contest.id).subscribe({
      next: stats => {
        this.stats = stats || new ContestPartStats();

        this.paymentStats = StatsHelper.chartData([
          this.stats.status_successful,
          this.stats.status_scheduled,
          this.stats.status_pending,
          this.stats.status_failed,
          this.stats.status_canceled,
        ]);
        this.gradeStats = StatsHelper.gradeChartData(this.stats);
        this.participantStatusStats = StatsHelper.userStatusChartData(this.stats);
        this.otherUserStatusStats = StatsHelper.chartData([
          this.stats.status_teacher_successful,
          this.stats.status_teacher_review,
          this.stats.status_teacher_rejected,
          this.stats.status_coordinator_successful,
        ]);
        this.examParticipantStatusStats = StatsHelper.chartData([
          this.stats.status_successful,
          this.stats.exam_participants,
        ]);
        this.examGradeStats = StatsHelper.chartData([
          this.stats.exam_school_grade_2,
          this.stats.exam_school_grade_3,
          this.stats.exam_school_grade_4,
          this.stats.exam_school_grade_5,
          this.stats.exam_school_grade_6,
          this.stats.exam_school_grade_7,
          this.stats.exam_school_grade_8,
          this.stats.exam_school_grade_9,
          this.stats.exam_school_grade_10,
          this.stats.exam_school_grade_11,
        ]);
        this.examPointsGradeStats = StatsHelper.chartData([
          this.stats.exam_points_grade_excellent,
          this.stats.exam_points_grade_good,
          this.stats.exam_points_grade_average,
        ]);
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|Stats fetch has failed message@@notifications-stats-fetch-failed-error:Statistical data fetch has failed`, error);
      }
    });
  }

  contestSelected(contest: Contest) {
    this.contest = contest;

    this.stats = undefined;
    this.paymentStats = undefined;
    this.gradeStats = undefined;
    this.participantStatusStats = undefined;
    this.otherUserStatusStats = undefined;
    this.examParticipantStatusStats = undefined;
    this.examGradeStats = undefined;
    this.examPointsGradeStats = undefined;

    this.loadContestStats(this.contest);
  }
}
